import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  StructuredDataDisplay,
  StructuredDataSchema,
  StructuredDataSchemaItem,
} from '@repo/shared/dtos/structured-data'
import { isEqual } from 'lodash'
import { GripVerticalIcon } from 'lucide-react'
import { useState } from 'react'

import { getItemId, getSectionId } from '@/components/admin/structure-data/display/helpers'
import { Menu } from '@/components/admin/structure-data/display/Menu'
import { Section } from '@/components/admin/structure-data/display/Section'
import { UnplacedItemsSection } from '@/components/admin/structure-data/display/UnplacedItemsSection'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'

export function Display({
  initialDisplay,
  initialSchema,
}: {
  initialDisplay: StructuredDataDisplay
  initialSchema: StructuredDataSchema
}) {
  const dispatch = useStoreDispatch()
  const [updatedDisplay, setUpdatedDisplay] = useState<StructuredDataDisplay>(initialDisplay)
  const [activeItem, setActiveItem] = useState<StructuredDataSchemaItem | null>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const placedItemIds = new Set(updatedDisplay.flatMap((section) => section.structuredDataIds))
  const unplacedItems = initialSchema.filter((item) => !placedItemIds.has(item.id))

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event
    const id = active.id as string
    const item = initialSchema.find((item) => id.endsWith(item.id))
    if (!item) return
    setActiveItem(item)
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      setActiveItem(null)
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    if (activeItem) {
      // Reorder items within the same section

      const activeItemId = getItemId(activeId)
      const overItemId = getItemId(overId)
      const activeSectionId = getSectionId(activeId)
      const overSectionId = getSectionId(overId)

      if (!activeSectionId || !overSectionId || !activeItemId) {
        setActiveItem(null)
        return
      }

      // Only reorder if in the same section
      if (activeSectionId === overSectionId && overItemId) {
        setUpdatedDisplay((prev) => {
          return prev.map((section) => {
            if (section.id === activeSectionId) {
              const oldIndex = section.structuredDataIds.indexOf(activeItemId)
              const newIndex = section.structuredDataIds.indexOf(overItemId)
              if (oldIndex !== -1 && newIndex !== -1) {
                return {
                  ...section,
                  structuredDataIds: arrayMove(section.structuredDataIds, oldIndex, newIndex),
                }
              }
            }
            return section
          })
        })
      }
      if (activeSectionId !== overSectionId) {
        // Moving item to a different section
        setUpdatedDisplay((prev) => {
          return prev.map((section) => {
            if (section.id === overSectionId) {
              // Add to target section if not already there
              if (!section.structuredDataIds.includes(activeItemId)) {
                return {
                  ...section,
                  structuredDataIds: [...section.structuredDataIds, activeItemId],
                }
              }
            } else if (section.id === activeSectionId) {
              // Remove from current section
              return {
                ...section,
                structuredDataIds: section.structuredDataIds.filter((id) => id !== activeItemId),
              }
            }
            return section
          })
        })
      }

      setActiveItem(null)
    } else {
      // Reorder sections
      const oldIndex = updatedDisplay.findIndex((s) => s.id === activeId)
      const newIndex = updatedDisplay.findIndex((s) => s.id === overId)
      if (oldIndex !== -1 && newIndex !== -1) {
        setUpdatedDisplay(arrayMove(updatedDisplay, oldIndex, newIndex))
      }
    }
  }

  const handleDelete = (sectionId: string): void => {
    const section = updatedDisplay.findIndex((s) => s.id === sectionId)
    if (section === -1) return
    setUpdatedDisplay((prev) => {
      const updatedDisplay = prev.filter((s) => s.id !== sectionId)
      void dispatch(adminStoreActions.organization.updateStructuredDataDisplay({ updatedDisplay }))
      return updatedDisplay
    })
  }

  return (
    <div className="flex gap-2">
      <Menu
        disabled={isEqual(initialDisplay, updatedDisplay)}
        onReset={() => setUpdatedDisplay(initialDisplay)}
        setUpdatedDisplay={setUpdatedDisplay}
        updatedDisplay={updatedDisplay}
      />

      <div className="mt-6 flex flex-1 flex-col gap-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={updatedDisplay.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            {updatedDisplay.map((section) => (
              <Section
                key={section.id}
                section={{
                  label: section.label,
                  id: section.id,
                  items: section.structuredDataIds
                    .map((id) => {
                      const item = initialSchema.find((item) => item.id === id)
                      if (!item) return
                      return item
                    })
                    .filter((item): item is StructuredDataSchemaItem => !!item),
                }}
                onDelete={handleDelete}
              />
            ))}

            <UnplacedItemsSection unplacedItems={unplacedItems} />
          </SortableContext>

          {activeItem && 'label' in activeItem && 'type' in activeItem && (
            <DragOverlay>
              <div className="border-primary-200 bg-primary-100 flex w-fit items-center gap-1 rounded-md border px-2 py-1 shadow-lg">
                <GripVerticalIcon className="text-primary-400 size-5" />
                <div className="text-sm">{activeItem.label}</div>
              </div>
            </DragOverlay>
          )}
        </DndContext>
      </div>
    </div>
  )
}
